import { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, useTheme } from "../App";
import { useRegion } from "../hooks/useRegion";
import PageSEO from "../components/marketing/PageSEO";
import "./LandingPage.css";

/**
 * LandingPage — tadlo.ai's public homepage.
 *
 * This is the `tadlo-homepage-v4` design, ported from the standalone HTML file
 * at frontend/tadlo-homepage-v4.html into the app rather than served as a
 * static file. Serving the raw HTML at `/` would have cost four things the
 * site already relies on:
 *
 *   1. PRERENDERING. scripts/prerender.js drives the SPA route in headless
 *      Chrome and writes build/index.html; nginx serves that to crawlers. A
 *      hand-maintained static file at the same path is what `app-shell.html`
 *      exists to avoid (see PRERENDER.md).
 *   2. SPA NAVIGATION. Every internal link below is a <Link>, so /pricing,
 *      /blog and /compare are client-side transitions, not full reloads.
 *   3. AUTH AWARENESS. A signed-in visitor gets /dashboard from the nav and
 *      every CTA; a signed-out one gets /auth. A static file cannot know.
 *   4. ONE SOURCE OF TRUTH for theme, currency and prices — see below.
 *
 * WIRING THAT DIFFERS FROM THE SOURCE HTML (deliberate, do not "restore"):
 *   - Theme: the standalone file kept its own `tadlo-theme` localStorage key
 *     and wrote data-theme on <html>. Here it reads useTheme() from App.js, so
 *     toggling on the homepage carries into the dashboard and back. The value
 *     is written onto the WRAPPER, which is what LandingPage.css keys off.
 *   - Currency: the source guessed India from the browser timezone. Here it
 *     comes from useRegion() -> GET /api/sub/region, the same source /pricing
 *     uses, so the two pages can never disagree. Both the ₹ and $ spans are
 *     always in the DOM and CSS reveals one, exactly as the source did.
 *   - Prices: read from GET /api/sub/plans (the Master Admin's plan_configs),
 *     not hardcoded. PLAN_CARDS' `fallback` values are only used if that call
 *     fails, and are set to the live config so an outage cannot invent a price.
 *   - Blog links: the source hardcoded eight /blog/* slugs, none of which
 *     exist. The Resources grid now renders real posts from
 *     GET /api/blog/public/homepage, and the footer's "compare" links point at
 *     the real /compare/vs-* pages.
 *
 * SEO NOTE: public/index.html already declares Organization, WebSite and
 * SoftwareApplication site-wide. The source HTML's @graph repeated all three;
 * emitting them again here would give the homepage two conflicting copies of
 * each entity, so only the HowTo and FAQPage blocks — which index.html does
 * NOT declare — are injected from this page.
 */

// ─── SVG PRIMITIVES ───────────────────────────────────
// The source markup repeats the same handful of inline SVGs dozens of times.
// One shared prop bag + small components keeps the JSX readable without
// changing a single path.

const STROKE = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

/** The thin tick used in feature lists, the hero fine print and plan bullets. */
const Tick = (props) => (
  <svg {...STROKE} strokeWidth={props.w || 2.4}>
    <path d="m4 12 5.5 5.5L20 7" />
  </svg>
);

/** The heavier tick used on the "Sent" cards and the follow-gate row. */
const TickBold = () => (
  <svg {...STROKE} strokeWidth={3}>
    <path d="m5 13 4 4L19 7" />
  </svg>
);

const Arrow = () => (
  <svg {...STROKE} strokeWidth={2.2}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const Sparkle = ({ full = true, style }) => (
  <svg {...STROKE} style={style}>
    <path d="m12 3 1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9Z" />
    {full && <path d="M19 15.5 19.7 17.3 21.5 18 19.7 18.7 19 20.5 18.3 18.7 16.5 18 18.3 17.3Z" />}
  </svg>
);

const Bolt = () => (
  <svg {...STROKE}>
    <path d="m13 2-9 12h7l-1 8 9-12h-7Z" />
  </svg>
);

const Chat = () => (
  <svg {...STROKE}>
    <path d="M21 11.5a8.5 8.5 0 0 1-12.4 7.6L3 21l1.9-5.6A8.5 8.5 0 1 1 21 11.5Z" />
  </svg>
);

const UserCheck = () => (
  <svg {...STROKE}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="m17 11 2 2 4-4" />
  </svg>
);

const Repeat = () => (
  <svg {...STROKE}>
    <path d="M17 2 21 6l-4 4" />
    <path d="M3 11V9a3 3 0 0 1 3-3h15" />
    <path d="M7 22 3 18l4-4" />
    <path d="M21 13v2a3 3 0 0 1-3 3H3" />
  </svg>
);

const Globe = () => (
  <svg {...STROKE}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a15 15 0 0 1 0 18 15 15 0 0 1 0-18Z" />
  </svg>
);

const Target = ({ dot = true }) => (
  <svg {...STROKE}>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="4.5" />
    {dot && <circle cx="12" cy="12" r="1" />}
  </svg>
);

const Star = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="m12 2 3 6.5 7 .9-5 4.9 1.2 7L12 18l-6.2 3.3L7 14.3l-5-4.9 7-.9Z" />
  </svg>
);

const VerifiedTick = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.2 14.4 4.5l3.2-.4.9 3.1 2.9 1.5-1.2 3 1.2 3-2.9 1.5-.9 3.1-3.2-.4L12 21.8 9.6 19.5l-3.2.4-.9-3.1-2.9-1.5 1.2-3-1.2-3 2.9-1.5.9-3.1 3.2.4Zm-.8 12.9 4.9-4.9-1.3-1.3-3.6 3.6-1.6-1.6-1.3 1.3Z" />
  </svg>
);

const Stars5 = () => (
  <div className="q__stars" aria-label="Rated 5 out of 5">
    {[0, 1, 2, 3, 4].map((i) => (
      <Star key={i} />
    ))}
  </div>
);

// ─── CONTENT ──────────────────────────────────────────

/**
 * Feature tiles. `slug` links the tile to its /features/{slug} marketing page.
 *
 * The eight slugged tiles are load-bearing: the SEO QA pass asserts the
 * homepage carries eight `home-feature-link-*` anchors that navigate to the
 * feature pages (test_reports/iteration_67.json). Renaming or unslugging one
 * breaks that check — and removes an internal link the feature page's ranking
 * depends on. The four without a slug have no page to point at yet.
 */
const FEATURE_TILES = [
  { icon: <svg {...STROKE}><rect x="4" y="8" width="16" height="12" rx="3" /><path d="M12 4v4M9 14h.01M15 14h.01" /></svg>, tone: "g-violet", title: "AI DM automation", desc: "Answers every DM in your voice — pricing, shipping, booking — even while you sleep.", slug: "dm-automation" },
  { icon: <Chat />, tone: "g-pink", title: "Comment triggers", desc: "Turn comments into leads in seconds, across unlimited posts and Reels at once.", slug: "comment-automation" },
  { icon: <svg {...STROKE}><path d="M12 20s-7-4.4-7-9.4A4.1 4.1 0 0 1 12 8a4.1 4.1 0 0 1 7 2.6c0 5-7 9.4-7 9.4Z" /></svg>, tone: "g-coral", title: "Story automation", desc: "Answer every story reply and poll response instantly, without opening the app.", slug: "story-reply-automation" },
  { icon: <Repeat />, tone: "g-amber", title: "Follow-up sequences", desc: "Nudge the people who never clicked, automatically, until they convert." },
  { icon: <UserCheck />, tone: "g-mint", title: "Follow gate", desc: "Deliver the link only after they follow you. Every download becomes a follower.", free: true },
  { icon: <svg {...STROKE}><path d="m12 3 9 5-9 5-9-5Z" /><path d="m3 13 9 5 9-5" /></svg>, tone: "g-blue", title: "Visual flow builder", desc: "Build branching funnels by dragging boxes around. No code, no flowchart degree.", slug: "flow-builder" },
  { icon: <Target />, tone: "g-mint", title: "Lead capture funnels", desc: "Ask for the email inside the DM, then export the whole list to CSV.", slug: "leads-inbox" },
  { icon: <svg {...STROKE}><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></svg>, tone: "g-violet", title: "Smart analytics", desc: "Comment → DM → click → lead. See exactly where people drop and why.", slug: "analytics" },
  { icon: <svg {...STROKE}><path d="M9 3h6M10 3v6.5L5.5 18A2.5 2.5 0 0 0 7.7 21.5h8.6A2.5 2.5 0 0 0 18.5 18L14 9.5V3" /></svg>, tone: "g-magenta", title: "A/B testing", desc: "Run two versions of a reply and keep whichever one people actually click.", slug: "ab-testing" },
  { icon: <svg {...STROKE}><path d="M10 13a5 5 0 0 0 7.5.5l3-3A5 5 0 0 0 13.5 3.5l-1.7 1.7" /><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3A5 5 0 0 0 10.5 20.5l1.7-1.7" /></svg>, tone: "g-blue", title: "Link-in-bio builder", desc: "A bio page that collects emails instead of just forwarding clicks elsewhere.", slug: "link-in-bio" },
  { icon: <Globe />, tone: "g-slate", title: "Multi-account", desc: "Run several Instagram accounts from one dashboard — built for agencies." },
  { icon: <Sparkle />, tone: "g-amber", title: "AI Reel analysis", desc: "Find out which hooks earned the reach, plus a weekly AI report and live trends." },
];

/** The four steps shown in the "One comment, start to finish" demo, per tab. */
const FLOWS = {
  comment: [
    "Someone comments your keyword",
    "Tadlo replies in about a second",
    "Follow-ups go out on their own",
    "Lead saved, follower gained",
  ],
  story: [
    "Someone replies to your story",
    "Tadlo answers instantly, in your voice",
    "Offer or link delivered automatically",
    "Lead saved, conversation logged",
  ],
  gate: [
    "Someone DMs your trigger word",
    "Tadlo checks whether they follow you",
    "They follow, the link is released",
    "Follower gained, lead saved",
  ],
};

const FLOW_TABS = [
  { id: "comment", label: "Comment → DM" },
  { id: "story", label: "Story → Reply" },
  { id: "gate", label: "DM → Follow gate" },
];

const TESTIMONIALS = [
  { initials: "SC", tone: "g-pink", photo: "/testimonials/sarah-chen.jpg", name: "Sarah Chen", role: "Fashion creator", metric: "+200 leads/month", before: "I was missing DMs and losing clients every single day.", after: "Now I get 20–30 leads weekly on autopilot. Tadlo changed everything." },
  { initials: "MJ", tone: "g-violet", photo: "/testimonials/marcus-johnson.jpg", name: "Marcus Johnson", role: "Fitness coach", metric: "3x engagement", before: "Replying to every comment and DM was eating up 4+ hours daily.", after: "Automated all my replies. Engagement went up 3x and I got my life back." },
  { initials: "PS", tone: "g-amber", photo: "/testimonials/priya-sharma.jpg", name: "Priya Sharma", role: "Agency owner", metric: "20h/week saved", before: "Managing 8 client accounts manually was unsustainable.", after: "Now all accounts run automated. We saved 20+ hours a week." },
];

const INCLUDED = [
  "Unlimited DMs",
  "Unlimited automations",
  "Follow gate",
  "Comment → DM & story replies",
  "Link-in-bio + lead capture",
  "Official Meta API",
  "No per-contact billing",
];

/**
 * The three plan cards.
 *
 * `planId` joins each card to a row of GET /api/sub/plans, which supplies the
 * NAME, the PRICE and the BADGE. The prose (`for`, `hook`, `adds`, `perks`) is
 * the v4 copy and stays here — plan_configs has no field for it.
 *
 * `fallback` is used only when that request fails. It mirrors the v4 pricing,
 * which as of Aug 2026 is also what plan_configs holds — see
 * backend/subscription/config.py. The two must be changed together.
 */
const PLAN_CARDS = [
  {
    planId: "free",
    name: "Free",
    fallback: { inr: 0, usd: 0 },
    tone: "g-mint",
    icon: <Chat />,
    for: "Get your first funnel live tonight",
    hook: "Free forever · No credit card · 1 Instagram account",
    adds: "What you also get",
    cta: "Start free",
    ctaClass: "btn--wire",
    perks: [
      "AI Reel analysis — 5 Reels",
      "Weekly AI report + trends access",
      "Standard DM delivery",
      "Small “via Tadlo” line on DMs",
    ],
  },
  {
    planId: "creator",
    name: "Creator",
    fallback: { inr: 249, usd: 3 },
    tone: "g-violet",
    icon: <svg {...STROKE}><path d="m12 3 3 6.3 6.5.9-4.7 4.6 1.1 6.7L12 18.3 6.1 21.5l1.1-6.7-4.7-4.6 6.5-.9Z" /></svg>,
    cardClass: "plan--pick",
    badgeClass: "plan__badge--pop",
    badge: "Most popular",
    for: "Make it look like your brand, not ours",
    hook: "Everything in Free, unbranded and faster",
    adds: "Everything in Free, plus",
    cta: "Get Creator",
    ctaClass: "btn--go",
    perks: [
      [<b key="b">No Tadlo branding</b>, " on any DM"],
      [<b key="b">Priority delivery</b>, " when a Reel spikes"],
      "Full DM flow editing + branching",
      "Re-trigger the same users",
      "Advanced AI + link-in-bio analytics",
      "20 AI Reel credits/month",
      "Priority support",
    ],
  },
  {
    planId: "pro",
    name: "Pro",
    fallback: { inr: 399, usd: 5 },
    tone: "g-amber",
    icon: <svg {...STROKE}><path d="m3 7 4.5 3.5L12 4l4.5 6.5L21 7l-1.8 11H4.8Z" /></svg>,
    cardClass: "plan--best",
    badgeClass: "plan__badge--best",
    badge: "Best value",
    for: "Run several accounts and prove the ROI",
    hook: "3 accounts for less than one coffee",
    adds: "Everything in Creator, plus",
    cta: "Get Pro",
    ctaClass: "btn--wire",
    perks: [
      [<b key="b">Up to 3 Instagram accounts</b>],
      [<b key="b">Conversion funnels</b>, " — see where leads drop"],
      "50 AI Reel credits/month",
      "Trend performance tracking",
      "Fastest DM delivery queue",
    ],
  },
];

/**
 * Price — one price and its conversion, in a form that survives being read as
 * plain text.
 *
 * THE BUG THIS REPLACES. The old markup was two sibling spans, one hidden by
 * CSS per region:
 *     <span class="inr">₹249</span><span class="usd">$5</span>/mo
 * A browser showed exactly one. Every crawler that extracts text ignores
 * `display:none` and read "₹249$5/mo" — a number that is not a price in any
 * currency. That string was on the homepage seven times, and it is the kind
 * of thing that makes an answer engine discard a source rather than misquote
 * it.
 *
 * THE SHAPE. The two prices stop being interchangeable alternatives and
 * become a price and its conversion:
 *     no CSS / no JS  ->  "₹249/mo (≈$5/mo)"     unambiguous in plain text
 *     data-cur="inr"  ->  "₹249/mo"
 *     data-cur="usd"  ->  "$5/mo"
 * Every rendered state is a SUBSET of the prerendered text, so no visitor
 * ever sees a price that was not in the HTML, and the HTML never states a
 * price no visitor can see.
 *
 * INR is primary because it is what customers are actually charged —
 * Razorpay is the only gateway, so even a US visitor's $5 is a conversion of
 * ₹249. The `≈` is therefore literally true, and is dropped for the free
 * plan, where ₹0 is exactly $0 and "approximately" would be false.
 */
function Price({ inr, usd, suffix = "" }) {
  const approx = String(inr) !== "0" ? "≈" : "";
  return (
    <>
      <span className="cur cur--inr">₹{inr}{suffix}</span>
      <span className="cur cur--sec"> ({approx}</span>
      <span className="cur cur--usd">${usd}{suffix}</span>
      <span className="cur cur--sec">)</span>
    </>
  );
}

const COMPARE_ROWS = [
  { label: "Free plan", us: [<Price key="p" inr="0" usd="0" />, " forever, full feature set"], manychat: "25 active contacts, 4 automations, branded", others: "Trial or none" },
  { label: "Paid entry price", us: [<Price key="p" inr="249" usd="5" suffix="/mo" />], manychat: "From $14/mo, rises with contacts", others: "$9.99–$19/mo" },
  { label: "Billing model", us: "Flat — no per-contact fees", manychat: "Per active contact, plus overages", others: "Flat, usually capped" },
  { label: "Active automations", us: "Unlimited on every plan", manychat: "4 on free, tiered above", others: "Usually capped" },
  { label: "Follow gate on free plan", us: "Yes", manychat: "No", others: "Rarely" },
  { label: "AI included", us: "Yes, on every plan", manychat: "Add-on, extra $29/mo", others: "Rarely offered" },
  { label: "Facebook Page required", us: "No — Instagram Login only", manychat: "Yes", others: "Varies" },
  { label: "Cost when a Reel goes viral", us: "Unchanged", manychat: "Tier jump or overage charges", others: "Unchanged" },
];

const MATRIX_ROWS = [
  ["Unlimited DMs", "yes", "yes", "yes"],
  ["Unlimited automations", "yes", "yes", "yes"],
  ["Comment → DM", "yes", "yes", "yes"],
  ["Story reply automation", "yes", "yes", "yes"],
  ["Follow gate", "yes", "yes", "yes"],
  ["Link-in-bio + lead capture", "yes", "yes", "yes"],
  ["Tadlo branding removed", "no", "yes", "yes"],
  ["Priority DM delivery", "no", "yes", "Fastest"],
  ["Full DM flow editing", "no", "yes", "yes"],
  ["Re-trigger same users", "no", "yes", "yes"],
  ["AI Reel credits / month", "5 Reels", "20", "50"],
  ["Advanced AI analytics", "no", "yes", "yes"],
  ["Conversion funnels", "no", "no", "yes"],
  ["Trend performance tracking", "no", "no", "yes"],
  ["Instagram accounts", "1", "1", "3"],
  ["Support", "Standard", "Priority", "Priority"],
];

const FAQS = [
  { question: "Is Tadlo free?", answer: "Yes. Tadlo has a free plan at ₹0 per month with no time limit and no credit card required. The free plan includes unlimited DMs, unlimited automations, comment-to-DM, story reply automation, the follow gate, and a link-in-bio page with lead capture. Paid plans at ₹249 and ₹399 per month add branding removal, deeper analytics, more AI Reel credits and multiple Instagram accounts." },
  { question: "Will Instagram DM automation get my account banned?", answer: "Not when the tool uses Instagram's official Messaging API. Tadlo sends every message through Instagram's official Private Replies and Messaging API — the same endpoints Meta built for businesses — with human-like delays and smart per-account rate limiting. Tadlo never logs into your account, never scrapes, and never simulates taps in the app. Bans typically come from tools that automate the Instagram app itself rather than the API." },
  { question: "What is comment-to-DM automation?", answer: "Comment-to-DM automation sends an Instagram direct message automatically when someone comments a chosen keyword on your post or Reel. You pick a post, pick a trigger word such as LINK or GUIDE, and write the DM. Every person who comments that word receives your message and link within seconds, without you touching the inbox." },
  { question: "What is an Instagram follow gate, and is it really free?", answer: "A follow gate checks whether the person who commented already follows you before sending the link. If they don't, Tadlo sends a short message asking them to follow first, then delivers the link once they do. It's included free on every Tadlo plan — most competing tools reserve follow gating for paid tiers." },
  { question: "Can Tadlo help my Reels go viral?", answer: "Comment-to-DM automation drives comments, and comments are one of the strongest engagement signals Instagram's ranking system uses. Asking viewers to comment a keyword to receive something typically lifts comment volume sharply, which increases the chance the Reel gets pushed to non-followers. Tadlo also includes AI Reel analysis and trends access so you can see which hooks and formats actually earned the reach." },
  { question: "How is Tadlo different from ManyChat?", answer: "ManyChat bills by active contacts, so your invoice rises when a post performs well, and its free plan is limited to 25 active contacts with ManyChat branding on messages. Tadlo charges a flat monthly price with unlimited DMs and unlimited automations on every plan, including free. Tadlo is also Instagram-only and connects through Instagram Login directly, so no linked Facebook Page is required." },
  { question: "Do I need a Facebook Page to use Tadlo?", answer: "No. Tadlo connects through Instagram Login directly. You need an Instagram Business or Creator account, but you don't need to create or link a Facebook Page. Setup takes about two minutes." },
  { question: "How many automations can I run?", answer: "Unlimited, on every plan including free. There's no cap on active automations, no cap on DMs sent, and no per-contact overage billing." },
  { question: "Can I collect leads and emails from Instagram?", answer: "Yes. Tadlo includes a link-in-bio builder with built-in lead capture, and you can ask for an email address inside a DM flow before delivering a link or lead magnet. Captured contacts land in your Leads section and export to CSV." },
  { question: "Who is Tadlo for?", answer: "Instagram creators delivering freebies and growing followers, coaches capturing and qualifying leads through conversations, e-commerce brands selling from DMs, and marketing agencies running comment-to-DM funnels across multiple client accounts." },
];

/**
 * Resource cards shown if GET /api/blog/public/homepage is unreachable.
 *
 * Every slug here is a REAL published post. The source HTML shipped six
 * invented slugs (/blog/instagram-dm-automation-guide and friends); all of
 * them 404 into the bio-slug catch-all, which is a dead link on the homepage
 * and a crawl error on the most-linked page of the site.
 */
const RESOURCES_FALLBACK = [
  { slug: "the-complete-guide-to-instagram-automation-in-2026-for-creators-businesses-agencies", title: "The complete guide to Instagram automation", excerpt: "Triggers, flows, limits and what actually converts" },
  { slug: "instagram-comment-to-dm-automation-the-fastest-way-to-capture-leads-in-2026", title: "Comment-to-DM automation for lead capture", excerpt: "Turning Reel comments into an email list" },
  { slug: "why-most-instagram-creators-struggle-to-monetize-and-how-automation-changes-everything", title: "Why most creators struggle to monetize", excerpt: "And how automation changes the maths" },
  { slug: "automate-instagram-dms-more-sales", title: "How to automate Instagram DMs for more sales", excerpt: "Reply instantly and convert conversations into customers" },
  { slug: "best-instagram-automation-strategies-2026", title: "Best Instagram automation strategies in 2026", excerpt: "The proven strategies top creators use to grow on autopilot" },
  { slug: "why-youre-overpaying-for-your-link-in-bio-tool-and-what-to-use-instead", title: "You're overpaying for your link-in-bio tool", excerpt: "What to use instead, and what it should cost" },
];

const HOWTO_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "@id": "https://tadlo.ai/#howto",
  name: "How to set up Instagram DM automation with Tadlo",
  totalTime: "PT2M",
  step: [
    { "@type": "HowToStep", position: 1, name: "Connect Instagram", text: "Sign in with Instagram Login and approve access. You need an Instagram Business or Creator account. No Facebook Page required." },
    { "@type": "HowToStep", position: 2, name: "Set your trigger", text: "Pick the post or Reel and the keyword people will comment, or choose a story reply or incoming DM keyword as the trigger." },
    { "@type": "HowToStep", position: 3, name: "Add a condition", text: "Turn on the follow gate so the link is delivered only after the person follows you, or ask for an email address first." },
    { "@type": "HowToStep", position: 4, name: "Publish", text: "Add your message and link and turn the automation on. Every matching comment gets a reply within seconds." },
  ],
};

// ─── HELPERS ──────────────────────────────────────────

/**
 * A testimonial avatar: coloured initials that a photo fades in over.
 *
 * The source used inline onerror="this.remove()". Ripping a node out from
 * under React invites a NotFoundError on the next reconcile, so the missing
 * -photo case is state here instead. The initials stay in the DOM either way,
 * which is also what a crawler and a screen reader get.
 */
function Avatar({ initials, tone, photo, name }) {
  const [state, setState] = useState("loading"); // loading | loaded | failed
  return (
    <span className={`avatar ${tone}`}>
      {initials}
      {state !== "failed" && (
        <img
          src={photo}
          alt={name}
          loading="lazy"
          className={state === "loaded" ? "is-loaded" : undefined}
          onLoad={() => setState("loaded")}
          onError={() => setState("failed")}
        />
      )}
    </span>
  );
}

/**
 * Count-up statistic.
 *
 * The literal figure is always the rendered text, and the animation only ever
 * overwrites it in a real browser. `navigator.webdriver` is true under
 * puppeteer, which is what scripts/prerender.js drives: without this guard the
 * snapshot can be taken mid-tween (SETTLE_MS is 900ms, the tween is 1400ms)
 * and build/index.html ships "7,412+" as the headline number.
 */
function Stat({ value, suffix, label, tone = "grad" }) {
  const ref = useRef(null);
  const [text, setText] = useState(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof navigator !== "undefined" && navigator.webdriver) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    if (!("IntersectionObserver" in window)) return undefined;

    const decimals = value % 1 !== 0 ? 1 : 0;
    let raf = 0;
    let start = null;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          const step = (ts) => {
            if (!start) start = ts;
            const p = Math.min((ts - start) / 1400, 1);
            const v = value * (1 - Math.pow(1 - p, 3));
            setText(
              v.toLocaleString("en-US", {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
              }) + suffix,
            );
            if (p < 1) raf = requestAnimationFrame(step);
          };
          raf = requestAnimationFrame(step);
        });
      },
      { threshold: 0.5 },
    );

    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [value, suffix]);

  return (
    <div>
      <p ref={ref} className={`stat__n ${tone}`}>
        {text ?? `${value.toLocaleString("en-US")}${suffix}`}
      </p>
      <p className="stat__l">{label}</p>
    </div>
  );
}

/** Renders a plan perk, which is either a plain string or an array of nodes. */
function Perk({ value }) {
  return (
    <li>
      <Tick w={2.6} />
      <span>{value}</span>
    </li>
  );
}

/** A comparison-matrix cell: "yes"/"no" become the tick/dash, anything else is text. */
function MatrixCell({ value, highlight }) {
  const cls = [highlight ? "us" : "", value === "yes" ? "yes" : value === "no" ? "no" : ""]
    .filter(Boolean)
    .join(" ");
  return <td className={cls || undefined}>{value === "yes" ? "✓" : value === "no" ? "—" : value}</td>;
}

// ─── PAGE ─────────────────────────────────────────────

export default function LandingPage() {
  const { isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { region } = useRegion();

  const [menuOpen, setMenuOpen] = useState(false);
  const [flow, setFlow] = useState("comment");
  const [faqOpen, setFaqOpen] = useState(0);
  const [plans, setPlans] = useState(null);
  const [resources, setResources] = useState(RESOURCES_FALLBACK);

  /**
   * Where every call to action goes.
   *
   * A signed-in visitor who lands back on the homepage should not be sent
   * through the login screen again — /dashboard is the useful destination.
   * `?mode=signup` opens AuthPage on the register tab (AuthPage reads the
   * param), which is the right first screen for a "Start free" click.
   */
  const startHref = isAuthenticated ? "/dashboard" : "/auth?mode=signup";
  const dashHref = isAuthenticated ? "/dashboard" : "/auth";
  const dashLabel = isAuthenticated ? "Dashboard" : "Log in";

  // scroll-padding-top / smooth scrolling belong to the scrolling element, so
  // they cannot live on the wrapper with the rest of the page's CSS. Removed
  // on unmount so no other route inherits an 84px scroll offset.
  useEffect(() => {
    document.documentElement.classList.add("tdv4-html");
    return () => document.documentElement.classList.remove("tdv4-html");
  }, []);

  useEffect(() => {
    const base = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

    fetch(`${base}/sub/plans`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data?.plans)) setPlans(data.plans);
      })
      .catch(() => {});

    fetch(`${base}/blog/public/homepage`)
      .then((r) => r.json())
      .then((posts) => {
        const usable = (Array.isArray(posts) ? posts : []).filter((p) => p?.slug && p?.title);
        if (usable.length > 0) setResources(usable.slice(0, 6));
      })
      .catch(() => {});
  }, []);

  // HowTo schema. FAQPage and the rest of <head/> are PageSEO's job; HowTo is
  // the one block it has no prop for.
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "ld-howto";
    el.text = JSON.stringify(HOWTO_SCHEMA);
    document.head.appendChild(el);
    return () => document.getElementById("ld-howto")?.remove();
  }, []);

  /** Plan name / price / badge as configured in plan_configs, card copy as designed. */
  const planFor = (card) => {
    const live = plans?.find((p) => p.id === card.planId);
    return {
      name: live?.name || card.name,
      inr: live ? live.price_inr : card.fallback.inr,
      usd: live ? live.price_usd : card.fallback.usd,
      badge: live?.badge || card.badge,
    };
  };

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="tdv4" data-theme={theme === "dark" ? "dark" : "light"} data-cur={region?.is_india ? "inr" : "usd"}>
      <PageSEO
        title="Instagram DM Automation Tool — Free Forever | Tadlo"
        description="Tadlo auto-replies to every Instagram comment, story reply and DM in seconds. Free follow gate, unlimited DMs and unlimited automations on every plan. Built on Instagram's official Messaging API — no bans, no per-contact billing."
        canonical="/"
        ogImage="https://tadlo.ai/tadlo-og.png"
        faqSchema={FAQS}
      />

      <a className="skip" href="#main">Skip to content</a>

      {/* ═══ NAV ═══ */}
      <header className="nav">
        <div className="wrap nav__in">
          <Link to="/" className="brand" aria-label="Tadlo — home">
            <span className="brand__logo"><img src="/tadlo-logo.jpeg" alt="Tadlo" width="52" height="52" /></span>
          </Link>
          <nav aria-label="Primary">
            <ul className="nav__links">
              <li><a href="#features">Features</a></li>
              <li><a href="#how">How it works</a></li>
              <li><a href="#use-cases">Use cases</a></li>
              <li><a href="#compare">Compare</a></li>
              <li><a href="#pricing">Pricing</a></li>
              <li><Link to="/blog">Blog</Link></li>
            </ul>
          </nav>
          <div className="nav__act">
            <button
              className="iconbtn"
              type="button"
              onClick={toggleTheme}
              aria-label="Switch between light and dark mode"
            >
              <svg className="ic-moon" {...STROKE} strokeWidth={1.9}><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" /></svg>
              <svg className="ic-sun" {...STROKE} strokeWidth={1.9}><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
            </button>
            <Link className="nav__dash" to={dashHref} data-testid="header-login-btn">{dashLabel}</Link>
            <Link className="nav__go" to={startHref} data-testid="header-cta-btn">Start free</Link>
            <button
              className="iconbtn burger"
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              aria-controls="mnav"
            >
              <svg {...STROKE} strokeLinejoin={undefined}><path d="M3 6h18M3 12h18M3 18h18" /></svg>
            </button>
          </div>
        </div>
        <nav className="mnav" id="mnav" data-open={menuOpen ? "true" : "false"} aria-label="Mobile">
          <ul>
            <li><a href="#features" onClick={closeMenu}>Features</a></li>
            <li><a href="#how" onClick={closeMenu}>How it works</a></li>
            <li><a href="#use-cases" onClick={closeMenu}>Use cases</a></li>
            <li><a href="#compare" onClick={closeMenu}>Compare</a></li>
            <li><a href="#pricing" onClick={closeMenu}>Pricing</a></li>
            <li><Link to="/blog" onClick={closeMenu}>Blog</Link></li>
            <li><Link to={dashHref} onClick={closeMenu}>{dashLabel}</Link></li>
          </ul>
          <Link className="btn btn--go btn--full" to={startHref} onClick={closeMenu}>Start free</Link>
        </nav>
      </header>

      <main id="main">
        {/* ═══ HERO ═══ */}
        <section className="hero">
          <span className="mesh hero__m1" aria-hidden="true" />
          <span className="mesh hero__m2" aria-hidden="true" />
          <span className="mesh hero__m3" aria-hidden="true" />
          <div className="wrap hero__grid">
            <div>
              <p className="pill">
                <Sparkle style={{ width: 15, height: 15 }} />
                AI-powered Instagram DM automation
              </p>
              <h1>Go viral on Instagram.<span className="l2 grad">Turn every comment into a follower and a lead.</span></h1>
              <p className="hero__sub">
                They comment your keyword. Tadlo DMs the link, checks they follow you first, and saves the lead — in about a second, every time. Four thousand comments or four, the work is the same.
              </p>
              <div className="hero__acts">
                <Link className="btn btn--go" to={startHref} data-testid="hero-cta-btn">
                  Start free — <Price inr="0" usd="0" /> forever
                  <Arrow />
                </Link>
                <a className="btn btn--wire" href="#how">
                  <svg {...STROKE}><path d="m6 4 13 8-13 8Z" /></svg>
                  See it work
                </a>
              </div>
              <div className="hero__fine">
                <span><Tick /> No credit card</span>
                <span><Tick /> No Facebook Page</span>
                <span><Tick /> Live in 2 minutes</span>
              </div>
            </div>

            {/* signature: two lanes — comments in, DMs out */}
            <div className="engine">
              {/* Meta Business Partner mark, sitting in the band of empty space
                  above the engine card. Hidden below 1000px, where the hero
                  collapses to one column and this would push the card a full
                  screen down on mobile. */}
              <img
                className="engine__partner"
                src="/meta-business-partner.jpeg"
                alt="Tadlo is a Meta Business Partner"
                width="200"
                height="70"
                loading="eager"
              />
              {/* The two floats are absolutely positioned against their
                  nearest positioned ancestor. That used to be .engine, which
                  now also contains the partner badge — so "3 leads captured"
                  landed on top of it. This stage re-anchors them to the card
                  they belong to. */}
              <div className="engine__stage">
              <span className="float float--tr">✦ 3 leads captured</span>
              <span className="float float--bl">⚡ avg 1.3s</span>
              <div className="engine__card" aria-label="Comments arriving on a Reel and the DMs Tadlo sends back automatically">
                <p className="engine__hd"><span className="dotlive" /> Reel · keyword “GUIDE” <b>automation on</b></p>
                <div className="lanes">
                  <div className="lane">
                    <p className="lane__t">Comments in</p>
                    <div className="cmt"><span className="cmt__av g-pink" /><span className="cmt__tx"><b>GUIDE</b> please 🙌<i>@ananya.writes</i></span></div>
                    <div className="cmt"><span className="cmt__av g-blue" /><span className="cmt__tx"><b>GUIDE</b><i>@thefitmarcus</i></span></div>
                    <div className="cmt"><span className="cmt__av g-amber" /><span className="cmt__tx">guide!! <b>GUIDE</b><i>@studio.priya</i></span></div>
                  </div>
                  <div className="bolt"><span><Bolt /></span></div>
                  <div className="lane lane--out">
                    <p className="lane__t">DMs out</p>
                    <div className="sent"><b><TickBold />Sent · 1.1s</b>Link delivered + email captured</div>
                    <div className="sent"><b><TickBold />Follow gate</b>Asked to follow → followed → link sent</div>
                    <div className="sent"><b><TickBold />Sent · 0.9s</b>Link delivered + lead saved</div>
                  </div>
                </div>
                <p className="engine__ft">
                  <span><i />Official Meta API</span>
                  <span><i />No daily caps</span>
                  <span><i />Runs while you sleep</span>
                </p>
              </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══ CHIPS ═══ */}
        <section style={{ paddingBottom: 76 }}>
          <div className="wrap chips">
            <span className="chip chip--v"><svg {...STROKE}><path d="M12 2.5 20 6v6c0 5-3.4 8.5-8 9.5-4.6-1-8-4.5-8-9.5V6Z" /><path d="m9 12 2 2 4-4" /></svg> Official Meta API</span>
            <span className="chip chip--m"><Chat /> Unlimited DMs</span>
            <span className="chip chip--p"><UserCheck /> Free follow gate</span>
            <span className="chip chip--a"><Bolt /> No account bans</span>
          </div>
        </section>

        {/* ═══ STATS ═══ */}
        <section className="band--sm stats">
          <div className="wrap">
            <div className="stats__grid">
              <Stat value={10000} suffix="+" label="Creators using Tadlo" />
              <Stat value={2} suffix="M+" label="DMs automated" />
              <Stat value={120} suffix="K+" label="Leads generated" tone="grad-mint" />
            </div>
          </div>
        </section>

        {/* ═══ ANSWER (AEO) ═══ */}
        <section className="band--sm">
          <div className="wrap">
            <div className="answer__box">
              <h2>What is Tadlo?</h2>
              <p>
                <strong>Tadlo is a free AI Instagram DM automation tool</strong> that sends a direct message automatically when someone comments a keyword on your post or Reel, replies to your story, or DMs you a trigger word. It runs on Instagram's official Messaging API, includes a free follow gate that delivers links only to people who follow you, and offers unlimited DMs and unlimited automations on every plan — including the free plan.
              </p>
              <p>
                Tadlo is built for Instagram creators, coaches, e-commerce brands and marketing agencies who want comment-to-DM funnels without per-contact billing. See the{" "}
                <Link to="/pricing">full pricing page</Link> for every plan, or{" "}
                <Link to="/compare">compare Tadlo against other tools</Link>.
              </p>
            </div>
          </div>
        </section>

        {/* ═══ VS ═══ */}
        <section className="band" style={{ background: "var(--bg-2)", borderBlock: "1px solid var(--line)" }}>
          <div className="wrap center">
            <p className="pill">The difference</p>
            <h2>Your Reel blows up at 2am. Now what?</h2>
            <p className="lede">Four thousand comments, one of you, and a link everybody wants. Here's how the two versions of that night go.</p>
          </div>
          <div className="wrap vs" style={{ textAlign: "left" }}>
            <div className="panel panel--bad">
              <p className="panel__hd">
                <svg {...STROKE} strokeLinejoin={undefined}><circle cx="12" cy="12" r="9" /><path d="m15 9-6 6M9 9l6 6" /></svg>
                Doing it by hand
              </p>
              <ul>
                {[
                  "You wake up to 4,000 unanswered comments",
                  "By the time you reply, they've moved on",
                  "Nobody followed, nothing was tracked",
                  "The best post you ever made earned you nothing",
                ].map((t) => (
                  <li key={t}>
                    <svg {...STROKE} strokeLinejoin={undefined}><path d="M12 8v5M12 16.5v.5" /><circle cx="12" cy="12" r="9" /></svg>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="panel panel--good">
              <p className="panel__hd"><svg {...STROKE}><path d="m5 13 4 4L19 7" /></svg> With Tadlo running</p>
              <ul>
                {[
                  "Every comment answered in about a second",
                  "Follow gate turns each delivery into a follower",
                  "Every lead captured, tagged and exportable",
                  "Unlimited DMs — the bill never moves",
                ].map((t) => (
                  <li key={t}><Tick /> {t}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ═══ META API ═══ */}
        <section className="band meta">
          <span className="mesh meta__m1" aria-hidden="true" />
          <span className="mesh meta__m2" aria-hidden="true" />
          <div className="wrap">
            <div className="meta__grid">
              <div>
                <p className="meta__tag">Account safety</p>
                <h2>Instagram<span className="l2 grad">Messaging API</span></h2>
                <p className="meta__sub">
                  Every Tadlo message runs on Instagram's official Private Replies and Messaging endpoints — the same infrastructure Meta built for business accounts. No logins, no scraping, no simulated taps in the app. That's why accounts don't get flagged.
                </p>
              </div>
              <div className="meta__badge">
                <svg
                  className="apibadge"
                  viewBox="0 0 480 250"
                  role="img"
                  aria-label="Tadlo runs on Instagram's official Messaging API, approved through Meta app review"
                >
                  <defs>
                    <linearGradient id="bgGrad" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#EEF3FF" />
                      <stop offset="55%" stopColor="#F6F0FF" />
                      <stop offset="100%" stopColor="#FFF1F7" />
                    </linearGradient>
                    <linearGradient id="igGrad" x1="0" y1="1" x2="1" y2="0">
                      <stop offset="0%" stopColor="#F5A524" />
                      <stop offset="35%" stopColor="#F4634E" />
                      <stop offset="70%" stopColor="#EC4899" />
                      <stop offset="100%" stopColor="#7C5CFF" />
                    </linearGradient>
                    <linearGradient id="tickGrad" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#3B82F6" />
                      <stop offset="100%" stopColor="#5B3DF5" />
                    </linearGradient>
                  </defs>

                  <rect width="480" height="250" rx="20" fill="url(#bgGrad)" />
                  <circle cx="72" cy="46" r="2.5" fill="#fff" opacity=".85" />
                  <circle cx="418" cy="206" r="2" fill="#fff" opacity=".8" />
                  <path d="M0 190c70-26 130 10 200-12s130-40 190-8v80H0Z" fill="#fff" opacity=".38" />

                  <rect x="52" y="58" width="376" height="134" rx="18" fill="#fff" />

                  <rect x="80" y="98" width="54" height="54" rx="16" fill="none" stroke="url(#igGrad)" strokeWidth="5" />
                  <circle cx="107" cy="125" r="13" fill="none" stroke="url(#igGrad)" strokeWidth="5" />
                  <circle cx="123.5" cy="108.5" r="3.6" fill="url(#igGrad)" />

                  <text x="152" y="118" fontFamily="Outfit, sans-serif" fontSize="26" fontWeight="800" fill="#131219" letterSpacing="-1">Instagram</text>
                  <text x="152" y="146" fontFamily="Outfit, sans-serif" fontSize="26" fontWeight="800" fill="#5B3DF5" letterSpacing="-1">Messaging API</text>
                  <text x="152" y="169" fontFamily="Inter, sans-serif" fontSize="12.5" fill="#8D8A9C">Approved through Meta app review</text>

                  <g transform="translate(392 68)">
                    <path d="M18 0 22.5 4.5 29 4 30.5 10.3 36 13.5 33.5 19.5 36 25.5 30.5 28.7 29 35 22.5 34.5 18 39 13.5 34.5 7 35 5.5 28.7 0 25.5 2.5 19.5 0 13.5 5.5 10.3 7 4 13.5 4.5Z" fill="url(#tickGrad)" />
                    <path d="m11 19.5 5 5 11-11" fill="none" stroke="#fff" strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round" />
                  </g>
                </svg>
                {/* Meta Business Partner mark, below the Instagram API badge.
                    The two say different things and both belong here: the SVG
                    is "we run on the official Messaging API", this is "we are
                    an enrolled Meta partner". */}
                <img
                  className="meta__partner"
                  src="/meta-business-partner.jpeg"
                  alt="Tadlo is a Meta Business Partner"
                  width="300"
                  height="105"
                  loading="lazy"
                />
                <p className="meta__cap">Official Meta infrastructure · No account bans</p>
              </div>
            </div>

            <div className="meta__pts">
              <article className="mpt">
                <div className="ico g-mint"><svg {...STROKE}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg></div>
                <h3>Human-like delays</h3>
                <p>Messages pace like a person replying, never like a bot firing in bursts.</p>
              </article>
              <article className="mpt">
                <div className="ico g-blue"><svg {...STROKE}><path d="M3 12h4l3 8 4-16 3 8h4" /></svg></div>
                <h3>Smart rate limiting</h3>
                <p>Per-account throttling stays inside Meta's limits even during a spike.</p>
              </article>
              <article className="mpt">
                <div className="ico g-violet"><Bolt /></div>
                <h3>Trigger-based only</h3>
                <p>Tadlo replies to people who engaged first. It never cold-DMs strangers.</p>
              </article>
              <article className="mpt">
                <div className="ico g-coral"><svg {...STROKE}><rect x="4" y="10" width="16" height="11" rx="2.5" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg></div>
                <h3>Revoke anytime</h3>
                <p>Disconnect from Instagram's settings whenever you want. Nothing locked in.</p>
              </article>
            </div>
          </div>
        </section>

        {/* ═══ SUITE ═══ */}
        <section className="band" id="features">
          <div className="wrap center">
            <p className="pill">Features</p>
            <h2>Complete automation suite</h2>
            <p className="lede">Everything you need to automate, engage and convert on Instagram — one login, one flat price, no separate AI tier.</p>
          </div>
          <div className="wrap suite">
            {FEATURE_TILES.map((f) => {
              const body = (
                <>
                  <div className={`ico ${f.tone}`}>{f.icon}</div>
                  <h3>{f.title}</h3>
                  <p>{f.desc}</p>
                  {f.free && <span className="tile__free">Free on every plan</span>}
                </>
              );
              // Slugged tiles become links to their /features page; the rest
              // stay plain <article>s so no dead route is ever linked.
              return f.slug ? (
                <Link
                  key={f.title}
                  className="tile"
                  to={`/features/${f.slug}`}
                  data-testid={`home-feature-link-${f.slug}`}
                >
                  {body}
                </Link>
              ) : (
                <article className="tile" key={f.title}>{body}</article>
              );
            })}
          </div>
        </section>

        {/* ═══ HOW ═══ */}
        <section className="band" id="how" style={{ background: "var(--bg-2)", borderBlock: "1px solid var(--line)" }}>
          <div className="wrap center">
            <p className="pill">Live demo</p>
            <h2>One comment, start to finish</h2>
            <p className="lede">Pick a trigger and watch what Tadlo does with it. Same four steps every time.</p>

            <div className="tabs" role="tablist" aria-label="Automation types">
              {FLOW_TABS.map((t) => (
                <button
                  key={t.id}
                  className="tab"
                  type="button"
                  role="tab"
                  aria-selected={flow === t.id}
                  onClick={() => setFlow(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* All THREE panels are rendered; only the selected one is shown.
                Previously this rendered FLOWS[flow] alone, so the story-reply
                and follow-gate walkthroughs existed nowhere in the HTML — the
                follow gate being the one thing competitors charge for. The
                inactive panels are hidden by the `hidden` attribute, enforced
                in CSS by `.flow[hidden]{display:none}` because .flow's own
                `display:flex` would otherwise beat the UA default. */}
            {FLOW_TABS.map((t) => (
              <div className="flow" key={t.id} hidden={flow !== t.id}>
                {[
                  { tone: "g-pink", icon: <Chat /> },
                  { tone: "g-amber", icon: <Bolt /> },
                  { tone: "g-blue", icon: <Repeat /> },
                  { tone: "g-mint", icon: <Target dot={false} /> },
                ].map((node, i) => (
                  // Fragment, not a wrapper div: .flow is the flex container and
                  // the nodes and arrows must be its direct children.
                  <Fragment key={i}>
                    <div className="fnode">
                      <span className={`ico ${node.tone}`}>{node.icon}</span>
                      <span>{FLOWS[t.id][i]}</span>
                    </div>
                    {i < 3 && <span className="farrow"><Arrow /></span>}
                  </Fragment>
                ))}
              </div>
            ))}

            <div style={{ marginTop: 44 }}>
              <Link className="btn btn--go" to={startHref} data-testid="how-cta-btn">
                Build your first automation free
                <Arrow />
              </Link>
            </div>
          </div>
        </section>

        {/* ═══ USE CASES ═══ */}
        <section className="band" id="use-cases">
          <div className="wrap center">
            <p className="pill">Use cases</p>
            <h2>Built for every Instagram business</h2>
            <p className="lede">Four different jobs, one shared problem: more people in the DMs than hours in the day.</p>
          </div>
          <div className="wrap who">
            <article className="aud aud--1">
              <div className="ico g-violet"><Sparkle full={false} /></div>
              <h3>Creators</h3>
              <p>Deliver the freebie and gain the follower in the same message. The gate does the asking for you.</p>
              <span className="aud__pill">Lead magnets &amp; growth</span>
            </article>
            <article className="aud aud--2">
              <div className="ico g-pink"><svg {...STROKE}><path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9.5" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.9" /></svg></div>
              <h3>Coaches</h3>
              <p>Qualify in the DM, then let only the serious ones onto your calendar.</p>
              <span className="aud__pill">Qualify &amp; book calls</span>
            </article>
            <article className="aud aud--3">
              <div className="ico g-amber"><svg {...STROKE}><path d="M3 17 9 11l4 4 8-8" /><path d="M15 7h6v6" /></svg></div>
              <h3>E-commerce</h3>
              <p>Price, sizes, shipping, codes. Answer once and every product post sells on its own.</p>
              <span className="aud__pill">Sell from the DM</span>
            </article>
            <article className="aud aud--4">
              <div className="ico g-mint"><Globe /></div>
              <h3>Agencies</h3>
              <p>Flat pricing across client accounts, so a client going viral doesn't eat your margin.</p>
              <span className="aud__pill">Multi-account &amp; reporting</span>
            </article>
          </div>
        </section>

        {/* ═══ TESTIMONIALS ═══ */}
        <section className="band" style={{ background: "var(--bg-2)", borderBlock: "1px solid var(--line)" }}>
          <div className="wrap center">
            <p className="pill">Testimonials</p>
            <h2>Real results, real users</h2>
            <p className="lede">What changed after switching the inbox to autopilot.</p>
          </div>
          <div className="wrap quotes">
            {TESTIMONIALS.map((t) => (
              <article className="q" key={t.name}>
                <Stars5 />
                <p className="q__ba q__before"><b>Before</b>“{t.before}”</p>
                <p className="q__ba q__after"><b>After</b>“{t.after}”</p>
                <span className="q__metric">{t.metric}</span>
                <div className="q__who">
                  <Avatar initials={t.initials} tone={t.tone} photo={t.photo} name={t.name} />
                  <div>
                    <p className="q__nm">{t.name} <VerifiedTick /></p>
                    <p className="q__rl">{t.role}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* ═══ COMPARE ═══ */}
        <section className="band" id="compare">
          <div className="wrap center">
            <p className="pill">Comparison</p>
            <h2>Tadlo vs ManyChat vs everyone else</h2>
            <p className="lede">
              Most tools bill by how many people engage with you, so the better a post does, the more you pay. Tadlo charges a flat price and never counts contacts.
            </p>
          </div>
          <div className="wrap tblwrap">
            <table>
              <caption>Instagram DM automation tools compared. Competitor pricing verified August 2026 — check each vendor's site for current rates.</caption>
              <thead>
                <tr>
                  <th scope="col">&nbsp;</th>
                  <th scope="col" className="us">Tadlo</th>
                  <th scope="col">ManyChat</th>
                  <th scope="col">Typical flat-rate tools</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((row) => (
                  <tr key={row.label}>
                    <th scope="row">{row.label}</th>
                    <td className="us">{row.us}</td>
                    <td>{row.manychat}</td>
                    <td>{row.others}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="wrap center" style={{ marginTop: 28 }}>
            <p className="lede" style={{ marginTop: 0 }}>
              Want the detail? Read the full{" "}
              <Link to="/compare/vs-manychat">Tadlo vs ManyChat</Link> breakdown, or{" "}
              <Link to="/compare">see every comparison</Link>.
            </p>
          </div>
        </section>

        {/* ═══ PRICING ═══ */}
        <section className="band" id="pricing" style={{ background: "var(--bg-2)", borderBlock: "1px solid var(--line)" }}>
          <div className="wrap center">
            <p className="pill">Pricing</p>
            <h2>The free plan is the whole product</h2>
            <p className="lede">Not a trial, not a teaser. Paid plans remove our branding and add scale — they don't unlock the basics.</p>

            <div className="included">
              <p className="included__t">On every plan, including free:</p>
              {INCLUDED.map((item) => (
                <span key={item}><Tick w={2.6} /> {item}</span>
              ))}
            </div>
          </div>

          <div className="wrap plans">
            {PLAN_CARDS.map((card) => {
              const live = planFor(card);
              return (
                <article
                  className={`plan ${card.cardClass || ""}`.trim()}
                  key={card.planId}
                  data-testid={`home-plan-${card.planId}`}
                >
                  {live.badge && (
                    <span className={`plan__badge ${card.badgeClass}`} data-testid={`home-plan-badge-${card.planId}`}>
                      {live.badge}
                    </span>
                  )}
                  <div className="plan__top">
                    <span className={`ico ${card.tone}`}>{card.icon}</span>
                    <div>
                      <h3>{live.name}</h3>
                      <p className="plan__for">{card.for}</p>
                    </div>
                  </div>
                  <p className="plan__price">
                    <Price inr={live.inr} usd={live.usd} />
                    <em> /month</em>
                  </p>
                  <p className="plan__hook">{card.hook}</p>
                  <p className="plan__adds">{card.adds}</p>
                  <ul>
                    {card.perks.map((perk, i) => (
                      <Perk key={i} value={perk} />
                    ))}
                  </ul>
                  <Link
                    className={`btn ${card.ctaClass} btn--full`}
                    to={startHref}
                    data-testid={`home-plan-cta-${card.planId}`}
                  >
                    {card.cta}
                  </Link>
                </article>
              );
            })}
          </div>

          <div className="wrap">
            <details className="matrix">
              <summary>Compare all features side by side</summary>
              <div className="tblwrap">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Feature</th>
                      <th scope="col">Free</th>
                      <th scope="col" className="us">Creator</th>
                      <th scope="col">Pro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {MATRIX_ROWS.map(([label, free, creator, pro]) => (
                      <tr key={label}>
                        <th scope="row">{label}</th>
                        <MatrixCell value={free} />
                        <MatrixCell value={creator} highlight />
                        <MatrixCell value={pro} />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
            <p className="center" style={{ marginTop: 22 }}>
              <Link to="/pricing">See the full pricing page</Link> for every plan, add-ons and billing detail.
            </p>
          </div>
        </section>

        {/* ═══ FAQ ═══ */}
        <section className="band faq">
          <div className="wrap wrap--nar center">
            <p className="pill">FAQ</p>
            <h2>Instagram DM automation, answered</h2>
          </div>
          <div className="wrap wrap--nar" style={{ marginTop: 44 }}>
            {FAQS.map((f, i) => (
              <details
                key={f.question}
                open={faqOpen === i}
                onToggle={(e) => {
                  if (e.currentTarget.open) setFaqOpen(i);
                  else if (faqOpen === i) setFaqOpen(null);
                }}
              >
                <summary>{f.question}</summary>
                <p>{f.answer}</p>
              </details>
            ))}
          </div>
        </section>

        {/* ═══ RESOURCES ═══ */}
        <section className="band--sm" style={{ background: "var(--bg-2)", borderBlock: "1px solid var(--line)", paddingBlock: 76 }}>
          <div className="wrap center">
            <p className="pill">Learn more</p>
            <h2>Guides on Instagram DM automation</h2>
          </div>
          <div className="wrap res">
            {resources.map((post) => (
              <Link key={post.slug} to={`/blog/${post.slug}`}>
                {post.title}
                {post.excerpt && <span>{post.excerpt}</span>}
              </Link>
            ))}
          </div>
          <div className="wrap center" style={{ marginTop: 28 }}>
            <Link className="btn btn--wire" to="/blog">Read every article<Arrow /></Link>
          </div>
        </section>

        {/* ═══ FINAL ═══ */}
        <section className="band final">
          <div className="wrap">
            <div className="final__box">
              <span className="mesh final__m" aria-hidden="true" />
              <h2>Every DM you miss is a lost customer.</h2>
              <p>Set up one automation tonight and let it work every time you post from here on.</p>
              <Link className="btn btn--white" to={startHref} data-testid="final-cta-btn">
                Start free with Instagram
                <Arrow />
              </Link>
              <p className="final__fine">No credit card · No Facebook Page · Unlimited DMs on the free plan</p>
            </div>
          </div>
        </section>
      </main>

      {/* ═══ FOOTER ═══ */}
      <footer className="foot">
        <div className="wrap">
          <div className="foot__grid">
            <div>
              <Link to="/" className="brand" aria-label="Tadlo — home">
                <span className="brand__logo brand__logo--sm"><img src="/tadlo-logo.jpeg" alt="Tadlo" width="40" height="40" /></span>
              </Link>
              <p className="foot__tag">
                AI Instagram DM automation for creators, coaches, e-commerce and agencies. Built on Instagram's official Messaging API.
              </p>
            </div>
            <div>
              <h4>Product</h4>
              <ul>
                <li><Link to="/features/comment-automation">Comment-to-DM automation</Link></li>
                <li><Link to="/features/story-reply-automation">Story reply automation</Link></li>
                <li><a href="#features">Instagram follow gate</a></li>
                <li><Link to="/features/link-in-bio">Link-in-bio builder</Link></li>
                <li><Link to="/pricing">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h4>Use cases</h4>
              <ul>
                <li><a href="#use-cases">For creators</a></li>
                <li><a href="#use-cases">For coaches</a></li>
                <li><a href="#use-cases">For e-commerce</a></li>
                <li><a href="#use-cases">For agencies</a></li>
              </ul>
            </div>
            <div>
              <h4>Compare</h4>
              <ul>
                <li><Link to="/compare/vs-manychat">Tadlo vs ManyChat</Link></li>
                <li><Link to="/compare/vs-linktree">Tadlo vs Linktree</Link></li>
                <li><Link to="/compare" data-testid="footer-compare-link">All comparisons</Link></li>
                <li><Link to="/trends">Trends</Link></li>
              </ul>
            </div>
            <div>
              <h4>Company</h4>
              <ul>
                <li><Link to="/blog">Blog</Link></li>
                <li><Link to="/privacy">Privacy policy</Link></li>
                <li><Link to="/terms">Terms of service</Link></li>
                <li><Link to="/data-deletion">Data deletion</Link></li>
                {/* There is no /contact route — it would fall through the
                    router's catch-all and render as a bio slug. */}
                <li><a href="mailto:contact@tadlo.ai">Contact</a></li>
              </ul>
            </div>
          </div>
          <p className="foot__base">
            © 2026 Tadlo. All rights reserved. Not affiliated with or endorsed by Meta Platforms, Inc. Instagram and Meta are trademarks of Meta Platforms, Inc.
          </p>
        </div>
      </footer>

      {/* ═══ MOBILE STICKY CTA ═══ */}
      <div className="mbar">
        <div>
          <p className="mbar__t"><Price inr="0" usd="0" /> forever</p>
          <p className="mbar__s">Unlimited DMs · No card</p>
        </div>
        <Link className="btn btn--go" to={startHref} data-testid="mobile-cta-btn">Start free</Link>
      </div>
    </div>
  );
}
